import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { Card, CardContent, PageSpinner, Input } from '@/components/ui';
import { Shield, ClipboardList, Search, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { BarCard } from '../components/BarCard';
import { useDebounce } from '@/hooks/useDebounce';

export function DashboardPage() {
  const { dbUser, isAdmin, managedBars } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search query to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Admins fetch bars with server-side search
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin-bars', debouncedSearch],
    queryFn: () =>
      barsApi.list({
        limit: 100,
        search: debouncedSearch || undefined,
      }),
    enabled: isAdmin,
    placeholderData: (previousData) => previousData,
  });

  // Use fetched bars for admins, managedBars for bar owners/managers
  const bars = isAdmin
    ? data?.bars || []
    : managedBars.map((mb) => ({
        id: mb.barId,
        name: mb.barName,
        role: mb.role,
      }));

  // For non-admins, still filter client-side since they only have a small list
  const filteredBars = useMemo(() => {
    if (isAdmin) return bars; // Admins use server-side search
    if (!searchQuery.trim()) return bars;
    const query = searchQuery.toLowerCase();
    return bars.filter((bar) => {
      const nameMatch = bar.name.toLowerCase().includes(query);
      const addressMatch = 'address' in bar && bar.address?.toLowerCase().includes(query);
      return nameMatch || addressMatch;
    });
  }, [bars, searchQuery, isAdmin]);

  const showSearch = bars.length > 20 || (isAdmin && data?.total && data.total > 20);

  // Early return AFTER all hooks
  if (isAdmin && isLoading) return <PageSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">
          {isAdmin ? 'Admin Dashboard' : 'My Bars'}
        </h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Welcome back, {dbUser?.name || dbUser?.email}
        </p>
      </div>

      {/* Admin-only: Quick Actions */}
      {isAdmin && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Link to="/admin/claim-requests">
            <Card className="transition-colors hover:border-accent-primary/50">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-500/10">
                  <ClipboardList className="h-6 w-6 text-yellow-400" />
                </div>
                <div>
                  <p className="font-medium">Claim Requests</p>
                  <p className="text-sm text-[var(--color-muted)]">
                    Review pending bar ownership claims
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-primary/10">
                <Shield className="h-6 w-6 text-accent-primary" />
              </div>
              <div>
                <p className="font-medium">Global Admin</p>
                <p className="text-sm text-[var(--color-muted)]">
                  You have access to all bars
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search box (when >20 bars) */}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" />
          <Input
            type="search"
            placeholder="Search bars by name or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {isFetching && searchQuery && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent-primary border-t-transparent" />
            </div>
          )}
        </div>
      )}

      {/* Bars grid */}
      {bars.length === 0 && !searchQuery ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-4 py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-border)]">
              <Building2 className="h-8 w-8 text-[var(--color-muted)]" />
            </div>
            <div className="text-center">
              <p className="font-medium">No bars yet</p>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                {isAdmin
                  ? 'No bars in the system yet'
                  : "You haven't claimed any bars yet. Visit a bar's page to claim it."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : filteredBars.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 py-8">
            <p className="text-[var(--color-muted)]">
              No bars match "{searchQuery}"
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredBars.map((bar) => (
            <BarCard
              key={bar.id}
              bar={bar}
              showRole={!isAdmin && 'role' in bar}
            />
          ))}
        </div>
      )}
    </div>
  );
}
